import { useState, useEffect, useCallback } from 'react';
import { offlineDB, syncManager } from '../utils/offlineStorage';

export function useOfflineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);

  // Check pending items
  const checkPendingSync = useCallback(async () => {
    try {
      const queue = await offlineDB.getSyncQueue();
      setPendingSync(queue.length);
    } catch {
      // IndexedDB may not be available
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    checkPendingSync();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkPendingSync]);

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && pendingSync > 0 && !isSyncing) {
      handleSync();
    }
  }, [isOnline]);

  const handleSync = async () => {
    if (isSyncing || !isOnline) return;
    setIsSyncing(true);
    try {
      await syncManager.syncPendingChanges();
      await checkPendingSync();
      setLastSyncTime(new Date().toISOString());
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  return {
    isOnline,
    pendingSync,
    isSyncing,
    lastSyncTime,
    sync: handleSync,
  };
}
